import {ComponentElementManifest, ComponentElementProperty} from "./ComponentElementManifest.model";
import {ComponentElementBorder, ComponentElementOverflow} from "../data_set/LookUp.model";
import {LookUp} from "../data_type/Lookup.model";
import {DataType} from "../model/DataType.model";

/**
 *  Component Element Common Interface
 * @since 0.0.1
 * @author Jongkwang Park, Heungsoo Ham
 * @interface
 */
export interface ComponentElementState  {
    /** destroyed  가 true 인 경우 async 함수 내 진행은 멈추어야 함: 자동 세팅 됨 */
    destroyed: boolean;
    /** accessibility  가 true 인 경우 section 508 준수: 자동 세팅 됨 */
    accessibility: boolean;
    /** <label for="">  등에 대응하기 위한 엘리먼트 아이디 등에 사용, 더불어 커스텀앨리먼트 인스턴스를 구별하기 위한 키로도 사용: 자동 세팅 됨*/
    elementId: string;
    /** app tool 등 application Id 별 행동이 필요한 컴퍼넌트: 자동 세팅 됨 */
    applicationId: string;
    /** app tool 등 form Id 별 행동이 필요한 컴퍼넌트: 자동 세팅 됨 */
    formId: string;
    /** 실제 사용되는 데이터 타입: 자동 세팅 됨 */
    dataType?: DataType;
    /** 현재 디스플레이모드 */
    displayMode?: ComponentElementDisplayMode;
    /** app tool 등 form Id 별 행동이 필요한 컴퍼넌트: 자동 세팅 됨 */
    defaultDateFormat?: string;
    /** 컴퍼넌트가 담기는 부모 돔: 반드시 세팅 할 것: 리액트 / 퓨어는 자동 세팅 됨 */
    parentDom: HTMLElement;
}

/**
 *  Component Element가 @@Form Core Engine 과 상호 통신하기 위한 공통 Interface
 *  on'이 붙어있는 함수들(onDisabled, onReadOnly, onPrint, onEdit)은 서로 배타적인 상태를 갖는다.
 *  예를 들어, onPrint 함수가 호출되면 기존의 상태(editable | disabled | readonly)는 printable 상태로 변경된다.
 * @since 0.0.1
 * @author Jongkwang Park, Heungsoo Ham
 * @interface
 */
export interface ComponentElementHandler<T> {
    /** 밸류 체인지 에밋 */
    emit?(value: T): void;
    /** 잘못된 종류의 밸류가 들어올 시 전파 */
    emitSubscriptionReject?(): void;
    /** 밸류 체인지 구독 */
    subscription?(value: T): void;
    /** 룩업 데이터 취득 */
    lookupDataSubscription?(lookup: Array<LookUp>, isLazyLoading?: boolean): void;

    /** These event handlers are called from @@Form Core Engine */
    onDisabled(): void;
    onReadOnly(): void;
    onPrint(): void;
    onEdit(): void;

    /** 컴퍼넌트가 준비되었을 때 한 번 호출 */
    readyComponent?(): void;

    /** 컴퍼넌트 관련 정보 취득 */
    getComponentInfo?(): ComponentElementState;

    /** 호출 시 컴퍼넌트는 포커스를 갖는다 */
    focus?(infoDom?: HTMLElement): void;

    /** 호출 시 컴포넌트 내의 모든 Overlay close */
    closeOverlay?(): void;

    /** 컴퍼넌트 앨리먼트 속성 전달 */
    setProperty(Property: ComponentElementProperty): void;

    /** 컴퍼넌트 앨리먼트 속성 구하기 */
    getProperty?(): void;

    /** 컴포넌트 내에서 에러 발생 시 전파 */
    emitDidCatch?(error: Error | any): void;

    /**
     * FormData 에밋
     * 컴포넌트 앨리먼트의 프로퍼티가 FormData 타입인 경우 값의 변경이 있으면
     * emitFormState 를 수행한다.
     * event 는 옵셔널: 버튼 등은 이벤트를 발생한 돔 객체를 전달해야 할 수 있다.
     */
    emitFormState?(value: string, event?: Event): void;

    /**
     * Selected Container 에밋
     * 컴포넌트가 여러개의 컨테이너를 포함하는 경우(예: 탭뷰) 선택된(보여지는) 컨테이너의
     * 키를 에밋한다.
     */
    emitContainerKey?(value: string): void;

    /**
     * Selected Container 에밋
     * 컴포넌트가 커스텀 컨테이너를 가지고 있을 수 있는 경우
     */
    emitHasContainerKey?(): void;

    /**
     * 부모가 자신의 디스플레이 속성을 자식이 승계하도록 설정된 경우에도
     * 컴퍼넌트가 부모의 디스플레이 속성을 무시해도 되는 겨우
     */
    noDisplayInheritance?(): void;

    /**
     * Resize event 구독
     * 컴포넌트 엘리먼트가 resize 이벤트에 대해 동작할 기능이 필요한 경우 구독, Optional
     */
    subscriptionResize?(e: Event): void;
}

/**
 * Component Element의 Life Cycle 관련 Interface
 * @since 0.0.1
 * @author Jongkwang Park, Heungsoo Ham
 * @interface
 */
interface ComponentElementLifeCycle {
    initDone(): void;
    destroy(): void;
    destroyDone(): void;
}

/**
 * export interface for ComponentElement
 * @since 0.0.1
 * @author Jongkwang, Park, Heungsoo Ham
 * @interface
 */
export interface ComponentElement<T> extends ComponentElementState, ComponentElementHandler<T>, ComponentElementLifeCycle {
}

/**
 * for static declaration
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface ComponentElementStatic<T> {
    /** 플러그인 타입 */
    readonly pluginType: string;
    /** 히든: 폼 컴퍼넌트 선택툴에서 제외 */
    readonly hidden?: boolean;
    /** 레이지 룩업 가능 여부 */
    readonly enableLazyLookup?: boolean;
    /** 컴퍼넌트 매니페스트 */
    readonly componentElementManifest: ComponentElementManifest;
    /**  Component Element의 Life Cycle 관련 Interface */
    init(parentDom: HTMLElement, elementId: string): ComponentElement<T>;
}

/**
 * Component Element의 Rendering 모드
 * INIT 모드에서는 렌더링 대상이 없다(return null)
 * @since 0.0.1
 * @author Jongkwang, Park
 * @enum
 */
export enum ComponentElementDisplayMode {
    INIT,
    EDIT,
    READONLY,
    DISABLED,
    PRINT
}

/**
 * primereact component 의 onChange type
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @type
 */
export type OnChangeFnType<T> = {
    (event: T): void;
};

/**
 * 컴포넌트의 상하좌우 보더 유무
 * ComponentElementBorder.NONE: 보더 없음
 * ComponentElementBorder.DEFAULT: 테마 기본 지원 유지
 *  만일 테마에서 보더를 기본 지원하지 않는다면 DEFAULT를 선택하더라도 보더는 없다.
 */
export type ComponentElementBorderType = {
    borderTop?: ComponentElementBorder;
    borderRight?: ComponentElementBorder;
    borderBottom?: ComponentElementBorder;
    borderLeft?: ComponentElementBorder;
};

/**
 * 컴포넌트의 overflow 속성 타입
 */
export type ComponentElementOverflowType = {
    overflow?: ComponentElementOverflow;
};

/**
 * 컴포넌트의 event 속성 타입
 */
export type ComponentEventType = {
    originalEvent: Event;
    value: any;
    checked?: boolean;
    index?: number
};

/**
 * Component Element에서 공통으로 사용되는 State 모델
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @type
 */
export type BaseStateModel = {
    show?: boolean;
    displayMode?: number;
};

/**
 * Javascript Object를 Map 자료구조 용도로 사용하기 위한 타입
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @type
 */
export type JSMap<T> = {
    [key: string]: T;
};